import React, { useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_ORGANIZATIONS } from '../graphql/queries';
import { CREATE_ORGANIZATION, UPDATE_ORGANIZATION, DELETE_ORGANIZATION } from '../graphql/mutations';

export const Organizations: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [form] = Form.useForm();

  const { loading, data, refetch } = useQuery(GET_ALL_ORGANIZATIONS);
  const [createOrganization] = useMutation(CREATE_ORGANIZATION);
  const [updateOrganization] = useMutation(UPDATE_ORGANIZATION);
  const [deleteOrganization] = useMutation(DELETE_ORGANIZATION);

  const handleAdd = () => {
    setEditingOrg(null);
    form.resetFields();
    form.setFieldsValue({ revenueShareRate: 0.15 });
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingOrg(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOrganization({ variables: { id } });
      message.success('Organization deleted successfully');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingOrg) {
        await updateOrganization({ variables: { id: editingOrg.id, input: values } });
        message.success('Organization updated successfully');
      } else {
        await createOrganization({ variables: { input: values } });
        message.success('Organization created successfully');
      }

      setIsModalVisible(false);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (phone: string) => phone || '-' },
    {
      title: 'Revenue Share',
      dataIndex: 'revenueShareRate',
      key: 'revenueShareRate',
      render: (rate: number) => `${(rate * 100).toFixed(0)}%`,
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Active' : 'Disabled'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Are you sure to delete this organization?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Organizations</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Organization
        </Button>
      </div>
      <Table loading={loading} dataSource={data?.getAllOrganizations || []} columns={columns} rowKey="id" />

      <Modal
        title={editingOrg ? 'Edit Organization' : 'Add Organization'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Address" name="address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Tax ID" name="taxId">
            <Input />
          </Form.Item>
          <Form.Item label="Revenue Share Rate" name="revenueShareRate" rules={[{ required: true }]}>
            <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
